import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import moment from "moment";

// const API_URL = process.env.REACT_APP_BASE_URL;
const TOKEN = "a94c7ac0083ae190f0fe61143a7547799b5334c4";

const headers = {
  Authorization: `Token ${TOKEN}`,
  "Content-Type": "application/json",
};

export const fetchGsts = createAsyncThunk(
  "gst/fetchGsts",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    if (filters && filters.length > 0) {
      // params.append("filters", JSON.stringify(filters));
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (
            filterField &&
            filterValue !== undefined &&
            filterValue !== null
          ) {
            if (typeof filterValue === "object" && filterValue !== null) {
              // Handle cases like { from_date: ..., to_date: ... }
              Object.entries(filterValue).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                  params.append(key, String(value));
                }
                if (
                  (filterValue.from_date || filterValue.to_date) &&
                  (filterField === "created_at" || filterField === "updated_at")
                ) {
                  if (filterField === "created_at") {
                    filterField = "created";
                  } else {
                    filterField = "updated";
                  }
                  params.append("filter_by", filterField);
                }
              });
            } else {
              // Handle primitive values (string, number)
              params.append(filterField, filterValue);
            }
          }
        }
      );
    }
    const response: any = await Axios.get(`/master/gst/?${params.toString()}`);

    return {
      data: response.data.data || response.data || [],
      rowCount: response.data.count || response.count || 0,
    };
  }
);

export const fetchGstById = createAsyncThunk(
  "gst/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(`/master/gst/${id}/get/`);

      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch GST by ID");
    }
  }
);

export const fetchGstByDropDown = createAsyncThunk(
  "Gst/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/master/gst/dropdown/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch Gst list");
    }
  }
);

export const updateGst = createAsyncThunk(
  "gst/updateGst",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(`/master/gst/${data.id}/update/`, data);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update GST");
    }
  }
);

export const updateStatusInGst = createAsyncThunk(
  "gst/updateStatusInGst",
  async (data: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: data.is_active };
      const response: any = await Axios.patch(
        `/master/gst/${data.id}/update/`,
        payload
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in GST"
      );
    }
  }
);

export const addGst = createAsyncThunk(
  "gst/addGst",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(`/master/gst/create/`, data);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add GST");
    }
  }
);

export const deleteGst = createAsyncThunk(
  "gst/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      await axios.delete(`/master/gst/${id}/`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// export const exportGstCSV = async () => {
//   const response = await Axios.get("/gst/?export=csv", {
//     responseType: "blob",
//   });

//   return {
//     blob: response.data,
//     filename:
//       response.headers["content-disposition"]
//         ?.split("filename=")[1]
//         ?.replace(/"/g, "") || "Gst.csv",
//   };
// };

export const exportGstCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  try {
    const params: any = {};

    if (search) {
      params.search = search;
    }

    if (filters && filters.length > 0) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (filterField && filterValue !== undefined && filterValue !== null) {
            if (typeof filterValue === "object" && filterValue !== null) {
              Object.entries(filterValue).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                  params[key] = String(value);
                }
                if (
                  (filterValue.from_date || filterValue.to_date) &&
                  (filterField === "created_at" || filterField === "updated_at")
                ) {
                  if (filterField === "created_at") {
                    filterField = "created";
                  } else {
                    filterField = "updated";
                  }
                  params.filter_by = filterField;
                }
              });
            } else {
              params[filterField] = filterValue;
            }
          }
        }
      );
    }

    // Try to get CSV blob first
    try {
      const response = await Axios.get("/master/gst/export/", {
        params,
        responseType: "blob",
      });

      return {
        blob: response.data,
        filename: `gst_${moment().format("YYYY-MM-DD")}.csv`,
      };
    } catch (blobError) {
      // If blob export fails, try JSON and convert to CSV
      const response = await Axios.get("/master/gst/", {
        params: { ...params, export: "excel" },
      });

      const data = response.data?.data || response.data || [];

      // Convert JSON to CSV
      if (data.length === 0) {
        throw new Error("No data to export");
      }

      // Define CSV headers based on GST structure
      const headers = [
        "Sr No.",
        "GST %",
        "Status",
        "Remarks",
        "Created By",
        "Created Date",
        "Updated By",
        "Updated Date",
      ];

      // Convert data to CSV rows
      const csvRows = [
        headers.join(","),
        ...data.map((gst: any, index: number) => {
          const row = [
            gst.sr_no || index + 1,
            gst.gst_percent ? `${gst.gst_percent}%` : "",
            gst.is_active ? "Active" : "Inactive",
            gst.remark || "",
            gst.created_by_user?.username || gst.created_by || "",
            gst.created_at ? moment(gst.created_at).format("DD/MM/YYYY") : "",
            gst.updated_by_user?.username || gst.updated_by || "",
            gst.updated_at ? moment(gst.updated_at).format("DD/MM/YYYY") : "",
          ];
          // Escape commas and quotes in CSV
          return row.map((cell) => {
            const cellStr = String(cell || "");
            if (cellStr.includes(",") || cellStr.includes('"') || cellStr.includes("\n")) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          }).join(",");
        }),
      ];

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      return {
        blob,
        filename: `gst_${moment().format("YYYY-MM-DD")}.csv`,
      };
    }
  } catch (error: any) {
    throw new Error(error.response?.data?.message || error.message || "Failed to export GST");
  }
};
